import { useApiQuery, mutateData } from '../../../api/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

export interface DuplicateTransaction {
  id: string;
  amount: number;
  payee: string;
  description: string;
  date: string;
  type: string;
  status: string;
  accountId: string;
  accountName: string | null;
  currency: string;
  categoryId: string | null;
  categoryName: string | null;
  categoryColor: string | null;
}

export interface DuplicateGroup {
  transactionIds: string[];
  transactions: DuplicateTransaction[];
  score: number;
  reason: string;
  amount: number;
  description: string;
  dateRange: { from: string; to: string };
  accountIds: string[];
}

interface DuplicatesResponse {
  groups: DuplicateGroup[];
  total: number;
}

const STORAGE_KEY = 'finlab:dismissed-duplicate-groups';

/** Create a stable key for a duplicate group based on its transaction IDs */
function groupKey(group: DuplicateGroup): string {
  return [...group.transactionIds].sort().join(',');
}

function getDismissedKeys(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function saveDismissedKeys(keys: Set<string>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...keys]));
}

export function dismissDuplicateGroup(group: DuplicateGroup): void {
  const keys = getDismissedKeys();
  keys.add(groupKey(group));
  saveDismissedKeys(keys);
}

export function clearDismissedDuplicates(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function useDuplicates(enabled = true) {
  const query = useApiQuery<DuplicatesResponse>(
    ['transactions', 'duplicates'],
    '/api/transactions/duplicates',
    {
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  // Filter out dismissed groups from the response
  const filteredData = useMemo(() => {
    if (!query.data) return query.data;
    const dismissed = getDismissedKeys();
    if (dismissed.size === 0) return query.data;
    const groups = query.data.groups.filter((g) => !dismissed.has(groupKey(g)));
    return { groups, total: groups.length };
  }, [query.data]);

  return { ...query, data: filteredData };
}

export function useDismissDuplicateGroup() {
  const queryClient = useQueryClient();

  return useCallback(
    (group: DuplicateGroup) => {
      dismissDuplicateGroup(group);
      queryClient.invalidateQueries({ queryKey: ['transactions', 'duplicates'] });
    },
    [queryClient],
  );
}

export function useDeleteDuplicateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      mutateData<void, unknown>(`/api/transactions/${id}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}
